import { useState } from "react";
import { useAuth } from "@/hooks/use-auth";
import { useMissionTypeSettings, useUpdateMissionTypeSetting } from "@/hooks/use-planning";
import { MISSION_PALETTE, PALETTE_LABELS, PALETTE_KEYS } from "@shared/missionPalette";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Settings2 } from "lucide-react";
import { Redirect } from "wouter";
import { getRoleLevel, ROLE_LEVEL } from "@shared/roles";

export default function TypesMissionPage() {
  const { profile } = useAuth();
  const { data: settings, isLoading } = useMissionTypeSettings();
  const update = useUpdateMissionTypeSetting();
  const { toast } = useToast();

  const [editing, setEditing] = useState<Record<string, { libelle: string; couleur: string }>>({});

  if (profile && getRoleLevel(profile.accessRole) < ROLE_LEVEL.admin) {
    return <Redirect to="/" />;
  }

  const getState = (typeKey: string, field: "libelle" | "couleur", fallback: string) =>
    editing[typeKey]?.[field] ?? fallback;

  const setField = (typeKey: string, field: "libelle" | "couleur", value: string) => {
    setEditing(prev => ({
      ...prev,
      [typeKey]: { libelle: getState(typeKey, "libelle", ""), couleur: getState(typeKey, "couleur", "gray"), [field]: value },
    }));
  };

  const handleSave = async (typeKey: string, originalLibelle: string, originalCouleur: string) => {
    const libelle = editing[typeKey]?.libelle ?? originalLibelle;
    const couleur = editing[typeKey]?.couleur ?? originalCouleur;
    try {
      await update.mutateAsync({ typeKey, libelle, couleur });
      setEditing(prev => { const n = { ...prev }; delete n[typeKey]; return n; });
      toast({ title: "Sauvegardé", description: `Type "${libelle}" mis à jour.` });
    } catch (e: any) {
      toast({ title: "Erreur", description: e.message, variant: "destructive" });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto p-6 space-y-6">
      <div className="flex items-center gap-3">
        <Settings2 className="w-6 h-6 text-primary" />
        <div>
          <h1 className="text-xl font-semibold">Types de mission</h1>
          <p className="text-sm text-muted-foreground">Personnalisez le libellé et la couleur de chaque type.</p>
        </div>
      </div>

      <div className="space-y-3">
        {(settings || []).map(setting => {
          const libelle = getState(setting.typeKey, "libelle", setting.libelle);
          const couleur = getState(setting.typeKey, "couleur", setting.couleur);
          const isDirty = libelle !== setting.libelle || couleur !== setting.couleur;
          const palette = MISSION_PALETTE[couleur] ?? MISSION_PALETTE.gray;

          return (
            <div
              key={setting.typeKey}
              className="rounded-xl border bg-card p-4 space-y-3"
              data-testid={`card-type-mission-${setting.typeKey}`}
            >
              <div className="flex items-center gap-3">
                <span
                  className="px-3 py-1 rounded-full text-sm font-medium"
                  style={{ backgroundColor: palette.bg, color: palette.text, border: `1px solid ${palette.border}` }}
                >
                  {libelle}
                </span>
                <code className="text-xs text-muted-foreground">{setting.typeKey}</code>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <div className="flex-1">
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">Libellé</label>
                  <Input
                    value={libelle}
                    onChange={e => setField(setting.typeKey, "libelle", e.target.value)}
                    data-testid={`input-libelle-${setting.typeKey}`}
                  />
                </div>

                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">Couleur</label>
                  <div className="flex flex-wrap gap-2">
                    {PALETTE_KEYS.map(key => {
                      const p = MISSION_PALETTE[key];
                      const isSelected = couleur === key;
                      return (
                        <button
                          key={key}
                          title={PALETTE_LABELS[key]}
                          onClick={() => setField(setting.typeKey, "couleur", key)}
                          data-testid={`swatch-${setting.typeKey}-${key}`}
                          className="w-7 h-7 rounded-full transition-transform hover:scale-110 focus:outline-none"
                          style={{
                            backgroundColor: p.bg,
                            border: isSelected ? `3px solid ${p.text}` : `2px solid ${p.border}`,
                            transform: isSelected ? 'scale(1.15)' : undefined,
                          }}
                        />
                      );
                    })}
                  </div>
                </div>
              </div>

              {isDirty && (
                <div className="flex justify-end gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setEditing(prev => { const n = { ...prev }; delete n[setting.typeKey]; return n; })}
                    data-testid={`button-cancel-${setting.typeKey}`}
                  >
                    Annuler
                  </Button>
                  <Button
                    size="sm"
                    disabled={update.isPending}
                    onClick={() => handleSave(setting.typeKey, setting.libelle, setting.couleur)}
                    data-testid={`button-save-${setting.typeKey}`}
                  >
                    {update.isPending && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
                    Enregistrer
                  </Button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
